import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", company: "", email: "", phone: "", website: "", industry: "", notes: "",
  status: "active", billing_model: "retainer", billing_amount: 0, billing_start_date: "",
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [report, setReport] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get("/projects"),
    ]);
    setClient(c.data);
    setProjects(p.data.filter(pr => pr.client_id === id));
  };
  useEffect(() => { load(); }, [id]);

  const generateReport = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/ai/client-report/${id}`);
      setReport(r.data.report);
      toast.success("Report generated");
    } catch (e) { toast.error("AI failed"); }
    finally { setAiLoading(false); }
  };

  const openEdit = () => {
    setForm({
      name: client.name || "", company: client.company || "", email: client.email || "",
      phone: client.phone || "", website: client.website || "", industry: client.industry || "",
      notes: client.notes || "", status: client.status || "active",
      billing_model: client.billing_model || "retainer",
      billing_amount: client.billing_amount ?? client.monthly_retainer ?? 0,
      billing_start_date: client.billing_start_date || "",
    });
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() && !form.company.trim()) { toast.error("Company or name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.billing_model !== "project_fee") payload.billing_start_date = null;
      await api.put(`/clients/${id}`, payload);
      toast.success("Client updated");
      setEditOpen(false);
      await load();
    } catch (e) { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      navigate("/clients");
    } catch (e) { toast.error("Delete failed"); setDeleting(false); }
  };

  if (!client) return <div className="p-8 text-sm text-neutral-500">Loading…</div>;

  return (
    <div data-testid="client-detail-page">
      <PageHeader
        eyebrow={<span><Link to="/clients" className="hover:underline">Clients</Link> / detail</span>}
        title={client.company || client.name}
        description={client.company ? client.name : client.email}
        actions={
          <>
            {isAdmin && (
              <>
                <Button onClick={openEdit} variant="outline" className="rounded-sm" data-testid="edit-client-btn">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button onClick={() => setDeleteOpen(true)} variant="outline"
                        className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="delete-client-btn">
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </>
            )}
            <Button onClick={generateReport} disabled={aiLoading} variant="outline" className="rounded-sm" data-testid="ai-report-btn">
              <Sparkles size={14} className="mr-1" /> {aiLoading ? "Writing…" : "AI report"}
            </Button>
          </>
        }
      />

      <Section className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
        {[
          { l: "Status", v: client.status },
          { l: "Industry", v: client.industry || "—" },
          {
            l: (client.billing_model || "retainer") === "retainer" ? "Monthly retainer" : "Project fee",
            v: (client.billing_amount ?? client.monthly_retainer ?? 0)
              ? `$${Number(client.billing_amount ?? client.monthly_retainer).toLocaleString()}`
              : "—",
          },
          { l: "Email", v: client.email || "—" },
        ].map((s) => (
          <div key={s.l} className="bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2">{s.l}</div>
            <div className="font-display text-base font-bold truncate">{s.v}</div>
          </div>
        ))}
      </Section>

      {report && (
        <Section>
          <div className="border border-neutral-900 bg-neutral-50 p-6" data-testid="ai-report-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} />
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold">AI engagement report · GPT-5.2</div>
            </div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{report}</div>
          </div>
        </Section>
      )}

      <Section>
        <div className="border border-neutral-200">
          <div className="px-4 py-3 border-b border-neutral-200 font-display font-bold">Tasks</div>
          {projects.length === 0 && <div className="p-6 text-xs text-neutral-500">No tasks for this client yet.</div>}
          {projects.map(p => (
            <Link key={p.id} to={`/projects/${p.id}`} className="block px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50" data-testid={`client-proj-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{p.status.replace("_", " ")}</div>
                </div>
                <div className="text-xs text-neutral-500">{p.description?.slice(0, 60) || ""}</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {client.notes && (
        <Section>
          <div className="border border-neutral-200 p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2">Notes</div>
            <div className="text-sm">{client.notes}</div>
          </div>
        </Section>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg rounded-sm" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
            <DialogDescription className="sr-only">Update client details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Company</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} data-testid="edit-client-company" /></div>
              <div><Label>Contact name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="edit-client-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Website</Label><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></div>
              <div><Label>Industry</Label><Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="edit-client-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-neutral-200 pt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2">Billing</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Billing model</Label>
                  <Select value={form.billing_model} onValueChange={(v) => setForm({ ...form, billing_model: v })}>
                    <SelectTrigger data-testid="edit-client-billing-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="retainer">Retainer (recurring monthly)</SelectItem>
                      <SelectItem value="project_fee">Project fee (one-time)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{form.billing_model === "retainer" ? "Monthly retainer ($)" : "Project fee ($)"}</Label>
                  <Input
                    type="number" min="0" step="100"
                    value={form.billing_amount}
                    onChange={e => setForm({ ...form, billing_amount: parseFloat(e.target.value) || 0 })}
                    data-testid="edit-client-billing-amount"
                  />
                </div>
              </div>
              {form.billing_model === "project_fee" && (
                <div className="mt-2">
                  <Label>Fee start date</Label>
                  <Input
                    type="date"
                    value={form.billing_start_date || ""}
                    onChange={e => setForm({ ...form, billing_start_date: e.target.value })}
                    data-testid="edit-client-billing-start"
                  />
                </div>
              )}
            </div>

            <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="save-client-btn">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="delete-client-dialog">
          <DialogHeader>
            <DialogTitle>Delete this client?</DialogTitle>
            <DialogDescription>
              Removing <span className="font-semibold">{client.name}</span> can&apos;t be undone.
              {projects.length > 0 && (
                <span className="block mt-2 text-rose-700 text-sm">
                  ⚠ {projects.length} linked {projects.length === 1 ? "project" : "projects"} will be left without a client.
                  Consider re-assigning {projects.length === 1 ? "it" : "them"} first.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={remove} disabled={deleting}
                    className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="confirm-delete-client-btn">
              {deleting ? "Deleting…" : "Delete client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
